import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Edit2, CheckCircle2, MessageCircle } from 'lucide-react'
import { getBookings, createBooking, updateBooking } from '../../services/staffServices'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'

const bookingApi = {
    getAll: () => getBookings(),
    create: (data) => createBooking(data),
    update: (id, data) => updateBooking(id, data),
}

const BOOKING_TYPES = {
    Booking: { label: 'Booking', color: 'bg-purple-500/15 text-purple-400' },
    Query: { label: 'Query', color: 'bg-slate-500/15 text-slate-400' },
    TEST_RIDE: { label: 'Test Ride', color: 'bg-blue-500/15 text-blue-400' },
    DEALER: { label: 'Dealer', color: 'bg-orange-500/15 text-orange-400' },
}

const STATUS_COLORS = {
    NEW: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    CONVERTED: 'bg-green-500/15 text-green-400 border-green-500/20',
}

const BRANDS = ['Mahindra', 'OLA', 'Ather', 'Ampere', 'TVS', 'Zelio']
const MODELS = ['Ola S1 Pro', 'TVS iQube', 'Zelio Eeva', 'Ampere Primus', 'Ather Rizta']

function BookingModal({ booking, profile, onClose, onSave }) {
    const isEdit = !!booking?.id
    const [form, setForm] = useState({
        customer_name: booking?.customer_name || '',
        customer_phone: booking?.customer_phone || '',
        address: booking?.address || '',
        brand: booking?.brand || '',
        model: booking?.model || '',
        variant: booking?.variant || '',
        color: booking?.color || '',
        booking_amount: booking?.booking_amount || '',
        payment_type: booking?.payment_type || '',
        booking_type: booking?.booking_type || 'TEST_RIDE',
        status: booking?.status || 'NEW',
    })
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900">
                    <h2 className="font-semibold text-white">{isEdit ? 'Edit Booking' : 'New Booking'}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
                </div>
                <form onSubmit={e => {
                    e.preventDefault()
                    if (!form.customer_name || !form.customer_phone || !form.brand || !form.model) {
                        toast.error('Name, phone, brand & model required'); return
                    }
                    onSave({
                        ...form,
                        booking_amount: form.booking_amount ? parseFloat(form.booking_amount) : null,
                        logged_by: profile?.name || 'STAFF',
                        logged_by_id: profile?.id || null,
                    })
                }} className="p-5 space-y-3">
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Type</label>
                        <div className="flex gap-2">
                            {['TEST_RIDE', 'DEALER', 'Booking', 'Query'].map(key => {
                                const val = BOOKING_TYPES[key]
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => set('booking_type', key)}
                                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                                            form.booking_type === key ? val.color + ' ring-1 ring-white/20' : 'bg-slate-800 text-slate-400'
                                        }`}
                                    >
                                        {val.label}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Name *</label>
                            <input value={form.customer_name} onChange={e => set('customer_name', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Phone *</label>
                            <input value={form.customer_phone} onChange={e => set('customer_phone', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Address</label>
                        <textarea value={form.address} onChange={e => set('address', e.target.value)} rows={2}
                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500 resize-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Brand *</label>
                            <select value={form.brand} onChange={e => set('brand', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500">
                                <option value="">Select brand</option>
                                {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Model *</label>
                            <select value={form.model} onChange={e => set('model', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500">
                                <option value="">Select model</option>
                                {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Variant</label>
                            <input value={form.variant} onChange={e => set('variant', e.target.value)}
                                placeholder="e.g. Pro"
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Colour</label>
                            <input value={form.color} onChange={e => set('color', e.target.value)}
                                placeholder="e.g. Matte Black"
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Booking Amount</label>
                            <input type="number" value={form.booking_amount} onChange={e => set('booking_amount', e.target.value)}
                                placeholder="e.g. 50000"
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Payment Type</label>
                            <select value={form.payment_type} onChange={e => set('payment_type', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500">
                                <option value="">Select</option>
                                <option value="CASH">Cash</option>
                                <option value="UPI">UPI</option>
                                <option value="CARD">Card</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Status</label>
                        <select value={form.status} onChange={e => set('status', e.target.value)}
                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500">
                            <option value="NEW">NEW</option>
                            <option value="CONVERTED">CONVERTED</option>
                        </select>
                    </div>
                    <div className="pt-3 flex gap-2">
                        <button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2.5 rounded-xl text-sm transition-colors">
                            {isEdit ? 'Update Booking' : 'Create Booking'}
                        </button>
                        <button type="button" onClick={onClose} className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-sm transition-colors">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

function StatusBadge({ status }) {
    const config = STATUS_COLORS[status] || 'bg-slate-500/15 text-slate-400 border-slate-500/20'
    return (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${config}`}>
            {status}
        </span>
    )
}

function BookingTypeBadge({ type }) {
    if (!type) return null
    const config = BOOKING_TYPES[type] || { label: type, color: 'bg-slate-500/15 text-slate-400' }
    return (
        <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${config.color}`}>
            {config.label}
        </span>
    )
}

export default function Bookings() {
    const queryClient = useQueryClient()
    const profile = useStaffAuthStore(s => s.profile)

    const { data: bookings, isLoading } = useQuery({
        queryKey: ['bookings'],
        queryFn: bookingApi.getAll,
    })

    const createMutation = useMutation({
        mutationFn: bookingApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] })
            toast.success('Booking created')
        },
        onError: (err) => toast.error(err.message || 'Create failed'),
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => bookingApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] })
            toast.success('Booking updated')
        },
        onError: (err) => toast.error(err.message || 'Update failed'),
    })

    const [showModal, setShowModal] = useState(false)
    const [editing, setEditing] = useState(null)

    const handleSave = (data) => {
        if (editing?.id) {
            updateMutation.mutate({ id: editing.id, data })
        } else {
            createMutation.mutate(data)
        }
        setShowModal(false)
        setEditing(null)
    }

    const handleConvert = (id) => {
        updateMutation.mutate({ id, data: { status: 'CONVERTED' } })
    }

    const handleWaClick = (phone) => {
        if (!phone) return
        const url = `https://wa.me/${phone.replace(/[^0-9]/g, '')}`
        window.open(url, '_blank')
    }

    const filtered = bookings || []

    if (isLoading) return (
        <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
    )

    return (
        <StaffLayout>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white">Bookings</h1>
                    <p className="text-slate-400 text-sm mt-1">Manage customer EV bookings & test rides</p>
                </div>
                <button
                    onClick={() => { setEditing(null); setShowModal(true) }}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors"
                >
                    <Plus size={16} /> New Booking
                </button>
            </div>

            <div className="bg-slate-900/50 border border-white/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-900/80 border-b border-white/5">
                            <tr>
                                <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">ID</th>
                                <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Customer</th>
                                <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">EV</th>
                                <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                                <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Amount</th>
                                <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                                <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {filtered.map(b => (
                                <tr key={b.id} className="hover:bg-white/5 transition-colors group">
                                    <td className="px-5 py-4">
                                        <div className="font-medium text-sm text-purple-400">{b.id?.slice(0, 8)}</div>
                                        <div className="text-xs text-slate-500">{(b.created_at || '').split('T')[0]}</div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="text-sm text-white">{b.customer_name || '-'}</div>
                                        <div className="text-xs text-slate-500">{b.customer_phone || '-'}</div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="text-sm text-white">
                                            {b.brand} {b.model} {b.variant}
                                        </div>
                                        {b.color && <div className="text-xs text-slate-500">{b.color}</div>}
                                    </td>
                                    <td className="px-5 py-4">
                                        <BookingTypeBadge type={b.booking_type} />
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="text-sm text-white">
                                            {b.booking_amount ? `₹${Number(b.booking_amount).toLocaleString('en-IN')}` : '-'}
                                        </div>
                                        {b.payment_type && <div className="text-xs text-slate-500">{b.payment_type}</div>}
                                    </td>
                                    <td className="px-5 py-4">
                                        <StatusBadge status={b.status} />
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => handleWaClick(b.customer_phone)}
                                                className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                                                title="WhatsApp"
                                            >
                                                <MessageCircle size={14} />
                                            </button>
                                            <button
                                                onClick={() => { setEditing(b); setShowModal(true) }}
                                                className="p-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
                                                title="Edit"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                            {b.status === 'NEW' && (
                                                <button
                                                    onClick={() => handleConvert(b.id)}
                                                    className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                                                    title="Mark Converted"
                                                >
                                                    <CheckCircle2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-5 py-12 text-center text-slate-500">
                                        No bookings yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <BookingModal
                    booking={editing}
                    profile={profile}
                    onClose={() => { setShowModal(false); setEditing(null) }}
                    onSave={handleSave}
                />
            )}
        </StaffLayout>
    )
}